import React, {useState, useEffect, useMemo} from "react";

import { Link } from 'react-router-dom';
import { Button, Table, message, Modal, Space, Upload } from "antd";
import { UploadOutlined, CheckCircleTwoTone, CloseCircleTwoTone, BarChartOutlined, ArrowRightOutlined, DatabaseOutlined, WarningTwoTone } from '@ant-design/icons';

import { getData, getLocationOrigin } from '../../config/api-connect';

import SummaryLayout from '../../components/layout/SummaryLayout'
import MonthSelector from "../../components/ui/MonthSelector";
import { useMonthFilter } from "../../hooks/useMonthFilter";

function PhonePeSummary() {
  const { selectedMonth, setSelectedMonth } = useMonthFilter();

  const [isUploadOpen, setIsUploadOpen] = useState(false)
  const [fileList, setFileList] = useState([])
  const [uploadLoading, setUploadLoading] = useState(false)
  const [summaryLoader, setSummaryLoader] = useState(false)
  const [summaryData, setSummaryData] = useState([])

  useEffect(() => {
    get_phonepe_summary()
  },[selectedMonth])

  const summaryTotals = useMemo(() => {
    if (!summaryData?.length) {
      return {
        phonepe_amount: 0,
        processed: 0,
        unprocessed: 0,
        system_collection: 0,
        unverified_collection: 0,
        verified_collection: 0,
      };
    }

    return summaryData.reduce(
      (acc, r) => {
        acc.phonepe_amount += r.phonepe_amount || 0;
        acc.processed += r.processed_amount || 0;
        acc.unprocessed += r.unprocessed_amount || 0;
        acc.system_collection += r.system_collection || 0;
        acc.unverified_collection += r.unverified_collection || 0;
        acc.verified_collection += r.verified_collection || 0;
        return acc;
      },
      {
        phonepe_amount: 0,
        processed: 0,
        unprocessed: 0,
        system_collection: 0,
        unverified_collection: 0,
        verified_collection: 0,
      }
    );
  }, [summaryData]);

  const formatNumber = (value) =>
    Math.round(value).toLocaleString("en-IN");

  const statsConfig = [
    { 
      key: "phonepe_amount",
      title: "PhonePe Collection",
      icon: <BarChartOutlined style={{ fontSize: 35, color: "#1890ff" }} />,
    },
    {
      key: "processed",
      title: "Processed",
      icon: <CheckCircleTwoTone twoToneColor="#52c41a" style={{ fontSize: 35 }} />,
    },
    {
      key: "unprocessed",
      title: "Unprocessed",
      icon: <CloseCircleTwoTone twoToneColor="#ff4d4f" style={{ fontSize: 35 }} />,
    },
    {
      key: "system_collection",
      title: "System Collection",
      icon: <DatabaseOutlined style={{ fontSize: 35, color: "#722ed1" }} />,
    },
    {
      key: "verified_collection",
      title: "Verified",
      icon: <CheckCircleTwoTone twoToneColor="#1565c0" style={{ fontSize: 35 }} />,
    },
    {
      key: "unverified_collection",
      title: "Unverified",
      icon: <WarningTwoTone twoToneColor="#faad14" style={{ fontSize: 35 }} />,
    },
  ];

  const handleOpen = () => {
    setIsUploadOpen(true)
    setFileList([])
    setUploadLoading(false)
  }
  
  const handleClose = () => {
    setIsUploadOpen(false)
    setFileList([])
    setUploadLoading(false)
  }

  const handleUpload = async () => {
    setUploadLoading(true)
    if (fileList.length === 0) {
      message.warning("Please select a file before uploading!");
      return;
    }

    const file = fileList[0].originFileObj;
    try {
      const data = await uploadFileToAPI(file);
      message.success(data.message || "File uploaded successfully!");
      handleClose()
    } catch (error) {
      message.error(error.message || "Something went wrong!");
    } finally {
      setUploadLoading(false);
      get_phonepe_summary()
    }
  };

  const handleFileChange = ({ fileList }) => {
    setFileList(fileList.slice(-1))
  };

  const uploadFileToAPI = async (file) => {
    const formData = new FormData();
    formData.append("file", file);

    const url = `${getLocationOrigin()}/finance/upload_phonepe_transactions_from_excel/`;
    const options = {
      method: "POST",
      body: formData,
    };

    const response = await fetch(url, options);
    if (!response.ok) throw new Error("Upload failed");
    return response.json();
  };

  const get_phonepe_summary = () => {
    setSummaryLoader(true)

    var month = selectedMonth.format('MM')
    var year = selectedMonth.format('YYYY')

    getData(`/finance/get_phonepe_venue_wise_summary/?month=${month}&year=${year}`)
    .then(response =>{
      setSummaryData(response.result)
      setSummaryLoader(false)
    })
    .catch(error => {
      setSummaryLoader(false)
      message.error(error.message || "Failed to fetch summary");
    });
  }

  const columns = [
    {
      title: "Venue",
      dataIndex: "venue_name",
    },
    // {
    //   title: "Store Name",
    //   dataIndex: "store_names",
    //   render: (value) => {
    //     if (!value) return "-";

    //     return value.split(",").map((store, index) => (
    //       <div key={index}>{store.trim()}</div>
    //     ));
    //   },
    // },
    {
      title: "PhonePe Collection",
      dataIndex: "phonepe_amount",
      align: "right",
      render: (value) => formatNumber(value),
    },
    {
      title: "Processed",
      dataIndex: "processed_amount",
      align: "right",
      render: (value) => formatNumber(value),
    },
    {
      title: "Unprocessed",
      dataIndex: "unprocessed_amount",
      align: "right",
      render: (value) => formatNumber(value),
    },
    {
      title: "System Collection",
      dataIndex: "system_collection",
      align: "right",
      render: (value) => formatNumber(value),
    },
    {
      title: "Verified",
      dataIndex: "verified_collection",
      align: "right",
      render: (value) => formatNumber(value),
    },
    {
      title: "Unverified",
      dataIndex: "unverified_collection",
      align: "right",
      render: (value) => formatNumber(value),
    },
    {
      title: "",
      width: 20,
      align: "center",
      render: (_, record) => {
        const month = selectedMonth.format("MM");
        const year = selectedMonth.format("YYYY");

        return (
          <Link to={`/phonepe-list/?venue_id=${record.venue}&month=${month}&year=${year}`}>
            <ArrowRightOutlined style={{ fontSize: "16px" }} />
          </Link>
        );
      }
    },
  ];

  const summaryCells = [
    { key: "phonepe_amount" },
    { key: "processed"},
    { key: "unprocessed"},
    { key: "system_collection"},
    { key: "verified_collection"},
    { key: "unverified_collection"},
  ];

  const renderSummary = () => (
    <Table.Summary.Row>
      <Table.Summary.Cell index={0}>
        <b>Total</b>
      </Table.Summary.Cell>

      {summaryCells.map((item, i) => (
        <Table.Summary.Cell
          key={item.key}
          index={i + 2}
          align="right"
        >
          <b>{formatNumber(summaryTotals[item.key])}</b>
        </Table.Summary.Cell>
      ))}
    </Table.Summary.Row>
  );

  const renderRightSideActions = () => (
    <>
      <Button 
        type="primary" 
        icon={<UploadOutlined />} 
        style={{ borderRadius: 8 , color: 'white', backgroundColor: '#1565c0', borderColor:'#1565c0'}}
        onClick={handleOpen}
      >
        Upload PhonePe Transaction List
      </Button>
      <Modal
        title="Upload PhonePe Payment List"
        open={isUploadOpen}
        onCancel={handleClose}
        confirmLoading={uploadLoading}
        footer={[
          <Button key="cancel" onClick={handleClose}>
            Cancel
          </Button>,
          <Button
            key="upload"
            type="primary"
            onClick={handleUpload}
            loading={uploadLoading}
            disabled={!fileList.length}
          >
            Upload
          </Button>,
        ]}
      >
        <Space direction="vertical" style={{ width: "100%" }}>
          <Upload
            accept=".xlsx"
            fileList={fileList}
            onChange={handleFileChange}
            beforeUpload={() => false} 
          >
            <Button icon={<UploadOutlined />}>Select .xlsx File</Button>
          </Upload>
        </Space>
      </Modal>

      <MonthSelector
        value={selectedMonth}
        onChange={setSelectedMonth}
      />
    </>
  )

  return (
    <SummaryLayout
      title="PhonePe Summary"
      headerContent={renderRightSideActions()}
      statsConfig={statsConfig}
      totals={summaryTotals}
      statsLoading={summaryLoader}
      formatter={formatNumber}
      tableProps={{
        rowKey: "venue",
        columns,
        data: summaryData,
        loading: summaryLoader,
        pagination: false,
      }}
      summary={summaryData.length ? renderSummary : undefined}
      extraContent={
        <div style={{ marginBottom: 10, fontSize: 12, color: "#666" }}>
          Note: <b>PhonePe Collection, Processed and Unprocessed</b> values are derived from upload data.
          <b> System Collection, Verified and Unverified</b> reflects actual system collection.
        </div>
      }
    />
  );
}

export default PhonePeSummary;
